import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../../Config";
import { IoClose } from "react-icons/io5";
import "./customer.css";

const CUSTOMER_TYPES = ["Defence", "Government", "Retail Outlet", "Private"];

const EditCustomer = ({ customerId, onClose, onSuccess, showSnackbar }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [customerData, setCustomerData] = useState({
    sap_code: "",
    customer_name: "",
    customer_type: "",
    email: "",
    contact_number: "",
    billing_address_line1: "",
    billing_address_line2: "",
    billing_city: "",
    billing_state: "",
    billing_pincode: "",
    shipping_address_line1: "",
    shipping_address_line2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_pincode: "",
    is_active: 1,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!customerId) return;

    const fetchCustomer = async () => {
      try {
        setLoadingData(true);
        const authKey = localStorage.getItem("authKey");
        const res = await axios.get(
          `${API_URL}/api/barrel/customers/${customerId}`,
          {
            headers: { Authorization: authKey },
          }
        );

        const c = res.data.customer || {};
        setCustomerData({
          sap_code: c.sap_code || "",
          customer_name: c.customer_name || "",
          customer_type: c.customer_type || "",
          email: c.email || "",
          contact_number: c.contact_number || "",
          billing_address_line1: c.billing_address_line1 || "",
          billing_address_line2: c.billing_address_line2 || "",
          billing_city: c.billing_city || "",
          billing_state: c.billing_state || "",
          billing_pincode: c.billing_pincode || "",
          shipping_address_line1: c.shipping_address_line1 || "",
          shipping_address_line2: c.shipping_address_line2 || "",
          shipping_city: c.shipping_city || "",
          shipping_state: c.shipping_state || "",
          shipping_pincode: c.shipping_pincode || "",
          is_active: c.is_active === 1 ? 1 : 0,
        });
      } catch (error) {
        console.error(error);
        showSnackbar("Failed to load customer data");
        onClose();
      } finally {
        setLoadingData(false);
      }
    };

    fetchCustomer();
  }, [customerId, onClose, showSnackbar]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    let valid = true;

    if (!customerData.customer_name.trim()) {
      newErrors.customer_name = "Customer name is required";
      valid = false;
    }
    if (!customerData.sap_code.trim()) {
      newErrors.sap_code = "SAP code is required";
      valid = false;
    }
    if (!customerData.customer_type.trim()) {
      newErrors.customer_type = "Customer type is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      showSnackbar("Please correct errors before saving");
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const res = await axios.put(
        `${API_URL}/api/barrel/customers/${customerId}`,
        customerData,
        { headers: { Authorization: authKey } }
      );

      if (res.status === 200) {
        showSnackbar("Customer updated successfully");
        onSuccess();
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar("Failed to update customer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === "barrels_popup-overlay") onClose();
  };

  if (loadingData) {
    return (
      <div className="barrels_popup-overlay">
        <div className="barrels_popup-modal">
          <div className="barrels_popup-body">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="barrels_popup-overlay" onClick={handleOverlayClick}>
      <div className="barrels_popup-modal">
        <div className="barrels_popup-header">
          <h4>Edit Customer</h4>
          <span className="barrels_close-btn" onClick={onClose}>
            <IoClose />
          </span>
        </div>

        <div className="barrels_popup-body_content">
        <div className="barrels_popup-body">
          <form>
            {/* Customer Name */}
            <div className="barrels_form-group">
              <label>
                Customer Name<span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="customer_name"
                value={customerData.customer_name}
                onChange={handleInputChange}
                placeholder="Enter customer name"
              />
              {errors.customer_name && (
                <span className="barrels_error">{errors.customer_name}</span>
              )}
            </div>

            {/* Contact Number */}
            <div className="barrels_form-group">
              <label>
                Contact Number
              </label>
              <input
                type="text"
                name="contact_number"
                value={customerData.contact_number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  handleInputChange({
                    target: { name: "contact_number", value: val },
                  });
                }}
                placeholder="Enter 10-digit contact number"
                maxLength="10"
              />
            </div>

            {/* Email */}
            <div className="barrels_form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={customerData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
              />
              {errors.email && (
                <span className="barrels_error">{errors.email}</span>
              )}
            </div>

            {/* SAP Code */}
            <div className="barrels_form-group">
              <label>
                SAP Code (Vendor Code)<span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="sap_code"
                value={customerData.sap_code}
                onChange={handleInputChange}
                placeholder="Enter SAP code"
              />
              {errors.sap_code && (
                <span className="barrels_error">{errors.sap_code}</span>
              )}
            </div>

            {/* Customer Type */}
            <div className="barrels_form-group">
              <label>
                Customer Type<span className="text-danger">*</span>
              </label>
              <select
                name="customer_type"
                value={customerData.customer_type}
                onChange={handleInputChange}
              >
                <option value="">Select customer type</option>
                {CUSTOMER_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.customer_type && (
                <span className="barrels_error">{errors.customer_type}</span>
              )}
            </div>

            {/* Billing Address */}
            <div className="barrels_address-section">
              <h5>Billing Address</h5>
              <div className="barrels_address-row">
                <input
                  type="text"
                  name="billing_address_line1"
                  placeholder="Address Line 1"
                  value={customerData.billing_address_line1}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="billing_address_line2"
                  placeholder="Address Line 2"
                  value={customerData.billing_address_line2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="barrels_address-row">
                <input
                  type="text"
                  name="billing_city"
                  placeholder="City"
                  value={customerData.billing_city}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="billing_state"
                  placeholder="State"
                  value={customerData.billing_state}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="billing_pincode"
                  placeholder="Pincode"
                  maxLength="6"
                  value={customerData.billing_pincode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    handleInputChange({
                      target: { name: "billing_pincode", value: v },
                    });
                  }}
                />
              </div>
            </div>

            {/* Shipping Address */}
            <div className="barrels_address-section">
              <h5>Shipping Address</h5>
              <div className="barrels_address-row">
                <input
                  type="text"
                  name="shipping_address_line1"
                  placeholder="Address Line 1"
                  value={customerData.shipping_address_line1}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="shipping_address_line2"
                  placeholder="Address Line 2"
                  value={customerData.shipping_address_line2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="barrels_address-row">
                <input
                  type="text"
                  name="shipping_city"
                  placeholder="City"
                  value={customerData.shipping_city}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="shipping_state"
                  placeholder="State"
                  value={customerData.shipping_state}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="shipping_pincode"
                  placeholder="Pincode"
                  maxLength="6"
                  value={customerData.shipping_pincode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    handleInputChange({
                      target: { name: "shipping_pincode", value: v },
                    });
                  }}
                />
              </div>
            </div>

            {/* <div className="barrels_form-group barrels_checkbox">
              <input
                type="checkbox"
                name="is_active"
                checked={!!customerData.is_active}
                onChange={(e) =>
                  setCustomerData((prev) => ({
                    ...prev,
                    is_active: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <label>Active</label>
            </div> */}
          </form>
        </div>
        </div>

        <div className="barrels_popup-footer">
          <button
            className="barrels_btn barrels_btn-save"
            onClick={handleSaveClick}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
          <button
            className="barrels_btn barrels_btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCustomer;
